<template>
    <div class="question-setting-section p-4">
        <!--Question type selection -->
        <XQuestionTypeSelect
            v-if="quizType != 'poll'"
            @input="changeQuestionType($event)"
            @open="onOpenSelectType"
            @close="onCloseSelectType"
            :value="question.question_type"
            :disabled="!canEditQuiz"
            placeholder="Select question type"
            id="question-type-select"
        />

        <ChartTypeSelect
            v-if="quizType == 'poll'"
            @input="selectChartType($event)"
            :chart-type="question.chart_type"
            :question-index="questionIndex"
        />

        <!-- Media section -->
        <section class="media-section">
            <!-- Video question -->
            <template v-if="quizType != 'poll' && question.question_type == 'video' && question.video">
                <div class="question-video-box mx-auto my-4 overflow-hidden relative">
                    <VideoPlayer
                        @loaded="$emit('video-loaded')"
                        :video-meta="question"
                        :video-path="question.video"
                        :time-duration="question.video_time"
                        ref="videoPlayer"
                        class="question-video"
                    />

                    <div
                        @click="
                            removeQuestionVideo()
                            removeQuestionImage()
                        "
                        :class="[
                            {
                                'remove-video-icon absolute': true,
                                'cursor-pointer': canEditQuiz,
                                'pointer-events-none cursor-default': !canEditQuiz
                            }
                        ]"
                    >
                        <i class="fas fa-times-circle text-white text-xl"></i>
                    </div>
                    <div
                        v-tippy="{ placement: 'top-center', arrow: true, arrowType: 'round', animation: 'fade' }"
                        @click="openUploadMedia(true)"
                        :content="$t('question_popup_edit_video')"
                        :class="[
                            {
                                'tool-icon edit-video-icon absolute': true,
                                'cursor-pointer': canEditQuiz,
                                'pointer-events-none cursor-default': !canEditQuiz
                            }
                        ]"
                        target="_blank"
                    >
                        <i class="fas fa-pen"></i>
                    </div>
                </div>
            </template>

            <!-- Hotspot question -->
            <template v-else-if="quizType != 'poll' && question.question_type == 'hotspot'">
                <div class="mt-8">
                    <HotspotSelection
                        v-if="question.image"
                        @add-hotspot-box="addHotspotAnswer()"
                        @hotspot-box="setHotspotData($event)"
                        @remove-image="removeQuestionImage()"
                        :question="question"
                        :class="{ 'pointer-events-none cursor-default': !canEditQuiz }"
                        color-code="#000"
                    />

                    <div
                        v-if="alertAnswerRequired || alertAnswerCorrectRequired || alertAnswerLimit"
                        class="has-error text-red text-center font-size-14 shadow-text-none"
                    >
                        <span v-if="alertAnswerRequired">* {{ $t('question_alert_select_one_choice') }}</span>
                        <span v-if="alertAnswerCorrectRequired">* {{ $t('question_alert_no_hotspot_correct') }}</span>
                        <span v-if="alertAnswerLimit">* {{ $t('question_alert_limit_hotspot_box') }}</span>
                    </div>
                </div>
            </template>

            <!-- Normal question -->
            <QuizCreatorImage
                v-else
                @remove="removeQuestionImage()"
                @upload-image="uploadQuestionImage($event)"
                :class="{ 'question-image': true, 'pointer-events-none': !canEditQuiz }"
                :id="`question-image-${questionIndex}`"
                :question-index="questionIndex"
                :image="question.image"
                :image-original="question.image_original"
                :image-source="question.image_source"
                :image-coordinates="question.image_coordinates"
                ref="questionImage"
                color="#ccc"
                border-color="#ccc"
                border-style="dashed"
                location="question"
            />
            <!-- Fullscreen selection -->
            <div
                v-if="
                    (question.question_type == 'basic' && question.image) ||
                    (question.question_type == 'video' && question.video && question.video_provider != 'youtube')
                "
                class="flex flex-col items-center mt-4"
            >
                <div class="flex flex-col md:flex-row row-gap-8 col-gap-8 md:items-center justify-center mt-2">
                    <XCheckbox
                        @input="onCheckFullScreen($event, 'desktop')"
                        :checked="question.fullscreen_desktop"
                        :disabled="!canEditQuiz"
                        type="checkbox"
                        size="md"
                        theme="dark"
                    >
                        <span class="font-size-14 flex items-center shadow-text-none">
                            {{ $t('question_label_fullscreen_desktop') }}
                        </span>
                    </XCheckbox>
                    <XCheckbox
                        @input="onCheckFullScreen($event, 'mobile')"
                        :checked="question.fullscreen_mobile"
                        :disabled="!canEditQuiz"
                        type="checkbox"
                        size="md"
                        theme="dark"
                    >
                        <span class="font-size-14 flex items-center shadow-text-none">
                            {{ $t('question_label_fullscreen_mobile') }}
                        </span>
                    </XCheckbox>
                    <XCheckbox
                        v-if="question.question_type === 'basic'"
                        @input="toggleFullWidthImageMobile($event)"
                        :checked="question.show_full_width_image_mobile"
                        :disabled="!canEditQuiz"
                        type="checkbox"
                        size="md"
                        theme="dark"
                    >
                        <span class="font-size-14 flex items-center shadow-text-none">
                            {{ $t('question_label_full_width_mobile') }}
                        </span>
                    </XCheckbox>
                </div>
            </div>
        </section>

        <!-- Question title section -->
        <section class="title-section mt-4">
            <div class="relative">
                <XInput
                    v-validate="{ required: !question.image || question.question_type == 'hotspot' }"
                    @input="inputQuestion($event)"
                    :value="question.title"
                    :name="`question-${questionIndex}`"
                    :disabled="!canEditQuiz"
                    :placeholder="
                        question.question_type != 'video'
                            ? $t('question_placeholder_question')
                            : $t('question_placeholder_video_question')
                    "
                    data-vv-as="question"
                    class="question-input"
                    autocomplete="off"
                    maxlength="200"
                />
                <span
                    @click="openUploadMedia()"
                    :class="{
                        'input-option-btn absolute': true,
                        'cursor-pointer': canEditQuiz,
                        'cursor-not-allowed': !canEditQuiz
                    }"
                >
                    <i
                        :class="{
                            'fas fa-video': question.question_type == 'video',
                            'far fa-image': question.question_type != 'video',
                            'image-icon shadow-text-none': true
                        }"
                    ></i>
                </span>
            </div>
            <span
                v-if="
                    errors.has(`question-${questionIndex}`) &&
                    ((!question.title &&
                        !question.image &&
                        question.question_type != 'reveal_image' &&
                        question.question_type != 'scale_image' &&
                        question.question_type != 'puzzle' &&
                        question.question_type != 'video') ||
                        ((!question.title || !question.image) && question.question_type == 'hotspot'))
                "
                class="has-error text-red text-center font-size-14 shadow-text-none mt-2"
            >
                * {{ errors.first(`question-${questionIndex}`) }}
            </span>
        </section>

        <!-- Validate section -->
        <section class="validate-section">
            <!-- Validate image if question has required image -->
            <template
                v-if="
                    question.question_type == 'reveal_image' ||
                    question.question_type == 'scale_image' ||
                    question.question_type == 'puzzle' ||
                    question.question_type == 'hotspot'
                "
            >
                <input
                    v-validate="'required'"
                    :value="question.image"
                    :name="`image-${questionIndex}`"
                    data-vv-as="image"
                    type="text"
                    hidden
                />
                <span
                    v-if="errors.has(`image-${questionIndex}`) && !question.image"
                    class="has-error text-red text-center font-size-14 shadow-text-none mt-2"
                >
                    * {{ errors.first(`image-${questionIndex}`) }}
                </span>
            </template>

            <!-- Validate video if question has required video -->
            <template v-if="question.question_type == 'video'">
                <input
                    v-validate="'required'"
                    :value="question.video"
                    :name="`video-${questionIndex}`"
                    data-vv-as="video"
                    type="text"
                    hidden
                />
                <span
                    v-if="errors.has(`video-${questionIndex}`) && !question.video"
                    class="has-error text-red text-center font-size-14 shadow-text-none mt-2"
                >
                    * {{ errors.first(`video-${questionIndex}`) }}
                </span>
            </template>
        </section>

        <!-- Question config section -->
        <section class="question-config-section">
            <!-- Puzzle block selection -->
            <div v-if="question.question_type == 'puzzle'" class="pieces-select-box flex items-center mt-4">
                <XCheckbox
                    @input="setPuzzlePieces(4)"
                    :name="`puzzle-box-select-${questionIndex}`"
                    :checked="question.puzzle_block == 4"
                    :disabled="!canEditQuiz"
                    id="puzzle_block4"
                    size="md"
                    theme="dark"
                >
                    <span class="font-size-14 font-bold shadow-text-none">4 {{ $t('question_puzzle_pieces') }}</span>
                </XCheckbox>
                <XCheckbox
                    @input="setPuzzlePieces(6)"
                    :name="`puzzle-box-select-${questionIndex}`"
                    :checked="question.puzzle_block == 6"
                    :disabled="!canEditQuiz"
                    id="puzzle_block6"
                    size="md"
                    theme="dark"
                >
                    <span class="font-size-14 font-bold shadow-text-none">6 {{ $t('question_puzzle_pieces') }}</span>
                </XCheckbox>
                <XCheckbox
                    @input="setPuzzlePieces(9)"
                    :name="`puzzle-box-select-${questionIndex}`"
                    :checked="question.puzzle_block == 9"
                    :disabled="!canEditQuiz"
                    id="puzzle_block9"
                    size="md"
                    theme="dark"
                >
                    <span class="font-size-14 font-bold shadow-text-none">9 {{ $t('question_puzzle_pieces') }}</span>
                </XCheckbox>
            </div>

            <!-- Music selection -->
            <template v-if="question.question_type == 'music'">
                <SpotifySettingSection
                    @open-spotify-popup="$emit('open-spotify-popup')"
                    :question="question"
                    :question-index="questionIndex"
                    :audio-spotify="audioSpotify"
                    :approval-pending="!canEditQuiz"
                    :class="{ 'pointer-events-none': !canEditQuiz }"
                />
                <input
                    :name="`music-${questionIndex}`"
                    :value="question.spotify_track"
                    type="text"
                    v-validate="'required'"
                    data-vv-as="music"
                    hidden
                />
                <span
                    v-if="errors.has(`music-${questionIndex}`) && !question.spotify_track"
                    class="has-error text-red text-center font-size-14 shadow-text-none mt-2"
                >
                    * {{ errors.first(`music-${questionIndex}`) }}
                </span>
            </template>
        </section>

        <!-- Custom message section -->
        <section
            v-if="
                question.question_type === 'sorting_list' ||
                question.question_type === 'typing_question' ||
                question.question_type === 'scramble'
            "
            class="custom-message-section mt-4"
        >
            <div class="flex justify-center items-center">
                <span
                    @click="showCustomMessage = !showCustomMessage"
                    class="font-bold text-center shadow-text-none cursor-pointer"
                >
                    <i :class="['fas mr-1', showCustomMessage ? 'fa-minus' : 'fa-plus']"></i>
                    {{ $t('question_header_custom_message') }}
                </span>
            </div>
            <XInput
                v-if="showCustomMessage"
                v-model="question.custom_message"
                @input="inputCustomMessage($event)"
                :disabled="!canEditQuiz"
                :placeholder="$t('question_placeholder_custom_message')"
                autocomplete="off"
                class="font-size-16 mt-4"
                type="text"
                maxlength="200"
            />
        </section>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import quizCreator from '@/mixins/quiz-creator.js'
import helper from '@/lib/helper.js'
import XQuestionTypeSelect from '@/components/v2/XQuestionTypeSelect'
import QuizCreatorImage from '@/components/quiz-creator/QuizCreatorImage'
import XInput from '@/components/v2/XInput'
import XCheckbox from '@/components/v2/XCheckbox'
import SpotifySettingSection from '@/components/quiz-creator/question-section/questions/SpotifySettingSection'
import VideoPlayer from '@/components/VideoPlayer'
import HotspotSelection from '@/components/HotspotSelection'
import ChartTypeSelect from '@/components/quiz-creator/question-section/questions/ChartTypeSelect'
import IntroHint from '@/lib/intro.js'
import introJsData from '@/mixins/introjs-data'

export default {
    components: {
        XQuestionTypeSelect,
        QuizCreatorImage,
        XInput,
        XCheckbox,
        SpotifySettingSection,
        VideoPlayer,
        HotspotSelection,
        ChartTypeSelect
    },

    mixins: [quizCreator, introJsData],

    props: {
        question: {
            type: Object,
            required: true
        },

        questionIndex: {
            type: Number,
            required: true
        },

        audioSpotify: {
            type: HTMLAudioElement
        }
    },

    data() {
        return {
            showCustomMessage: false,
            alertAnswerRequired: false,
            alertAnswerCorrectRequired: false,
            alertAnswerLimit: false,
            introHint: new IntroHint(),
            videoPath: ''
        }
    },

    created() {
        if (this.question.custom_message) {
            this.showCustomMessage = true
        }
    },

    watch: {
        question: {
            handler(data) {
                this.videoPath = data.video

                if (
                    data.question_type != 'sorting_list' &&
                    data.question_type != 'typing_question' &&
                    data.question_type != 'scramble'
                ) {
                    this.showCustomMessage = false

                    if (data.custom_message) {
                        this.$store.commit('updateQuestionCustomMessage', {
                            value: '',
                            questionIndex: this.questionIndex
                        })
                    }
                }
            },
            deep: true
        },

        showCustomMessage() {
            this.$emit('show-custom-message')
        }
    },

    computed: {
        ...mapState({
            quizType: state => state.question.type,
            isCreateNewQuiz: state => state.isCreateNewQuiz
        }),

        ...mapGetters({
            orderAbility: 'orderAbility'
        })
    },

    methods: {
        removeHint() {
            this.introHint.removeHints()
        },

        onOpenSelectType() {
            this.removeHint()
        },

        callHints(timeout) {
            if (this.quizType != 'poll' && this.questionIndex == 0) {
                this.removeHint()
                setTimeout(() => {
                    this.introHint.register(this.hintsCreateQuestion)
                }, timeout)
            }
        },

        onCloseSelectType() {
            this.callHints(500)
        },

        changeQuestionType(value) {
            this.callHints(500)

            if (!this.canEditQuiz) return

            const questionIndex = this.questionIndex
            const previousType = this.question.question_type
            const currentType = value.code

            if (previousType == 'puzzle' || previousType == 'hotspot') {
                this.$store.commit('resetAnswer', questionIndex)
            }

            if (previousType == 'video') {
                this.removeQuestionVideo()
                this.removeQuestionImage()
            } else {
                this.removeQuestionImage()
            }

            if (currentType == 'puzzle') {
                this.question.answers = []
                this.setPuzzlePieces(4)
            }

            if (currentType == 'hotspot') {
                this.question.answers = []
            }

            if (currentType == 'typing_question') {
                this.$store.commit('sliceAnswerTypingQuestion', questionIndex)
            }

            if (currentType == 'scramble') {
                this.$store.commit('sliceAnswerTypingQuestion', questionIndex)

                const answer = this.question.answers[0].title
                this.$store.commit('updateAnswerTitle', {
                    value: answer.toUpperCase(),
                    questionIndex,
                    answerIndex: 0
                })
            }

            this.$store.commit('toggleQuestionType', { questionIndex, type: currentType })

            this.$store.commit('setFullScreenDesktop', { value: false, questionIndex: this.questionIndex })
            this.$store.commit('setFullScreenMobile', { value: false, questionIndex: this.questionIndex })

            this.errors.clear()
        },

        selectChartType(type) {
            this.$store.commit('updateChartType', { questionIndex: this.questionIndex, value: type })
        },

        inputQuestion(event) {
            if (!this.canEditQuiz) return

            let question = event.target.value

            if (!helper.trim(question).length) {
                question = ''
            }

            this.$store.commit('updateQuestionTitle', { value: question, questionIndex: this.questionIndex })
        },

        openUploadMedia(isEdit) {
            if (!this.canEditQuiz) return

            const mediaObject = {
                open: true,
                location: 'question',
                questionIndex: this.questionIndex
            }

            if (this.question.question_type == 'video') {
                mediaObject.questionVideo = true

                if (isEdit) {
                    mediaObject.video = this.question.video
                    mediaObject.image = this.question.image
                    mediaObject.video_provider = this.question.video_provider
                    mediaObject.video_time = this.question.video_time
                    mediaObject.video_mute = this.question.video_mute
                    mediaObject.video_loop = this.question.video_loop
                    mediaObject.isEdit = isEdit

                    this.$refs.videoPlayer.stopVideo()
                }
            }

            this.$store.commit('toggleOpenUploadMedia', mediaObject)
        },

        onCheckFullScreen(event, type) {
            if (!this.canEditQuiz) return

            const value = event.target.checked

            if (type == 'desktop') {
                this.$store.commit('setFullScreenDesktop', { value, questionIndex: this.questionIndex })
            }

            if (type == 'mobile') {
                this.$store.commit('setFullScreenMobile', { value, questionIndex: this.questionIndex })
            }
        },

        setPuzzlePieces(pieces) {
            if (!this.canEditQuiz) return

            this.$store.commit('puzzleQuestionStatus', { questionIndex: this.questionIndex, value: pieces })
        },

        addHotspotAnswer() {
            if (!this.canEditQuiz) return

            this.$store.commit('addNewAnswer', this.questionIndex)
            this.alertAnswerRequired = false
        },

        setHotspotData(data) {
            if (!this.canEditQuiz) return

            this.$store.commit('updateHotspotData', { data, questionIndex: this.questionIndex })
            this.checkAnswersRequired()
        },

        inputCustomMessage(event) {
            if (!this.canEditQuiz) return

            let customMessage = event.target.value

            if (!helper.trim(customMessage).length) {
                customMessage = ''
            }

            this.$store.commit('updateQuestionCustomMessage', {
                value: customMessage,
                questionIndex: this.questionIndex
            })
        },

        checkAnswersRequired() {
            let countIsCorrect = 0

            this.question.answers.forEach(item => {
                if (item.is_correct) {
                    countIsCorrect++
                }
            })

            if (countIsCorrect > 1 && this.question.answers_required == null) {
                this.toggleAnswersRequired('one', this.questionIndex)
            }

            if (countIsCorrect === 1 && this.question.answers_required != null) {
                this.toggleAnswersRequired(null, this.questionIndex)
            }

            this.$store.commit('updateAnswersCount', { countIsCorrect, questionIndex: this.questionIndex })
        },

        toggleAnswersRequired(value) {
            this.$store.commit('toggleAnswersRequired', { value, questionIndex: this.questionIndex })
        },

        async validateQuestionSection() {
            const passed = await this.$validator.validate()

            const payload = {
                section: 'question',
                questionIndex: this.questionIndex,
                passed
            }

            this.$emit('validate-result', payload)

            return true
        },

        validateHotspotQuestion() {
            this.alertAnswerRequired = false
            this.alertAnswerCorrectRequired = false
            this.alertAnswerLimit = false

            let passed = true

            if (!this.question.answers.length) {
                this.alertAnswerRequired = true
                passed = false
            } else if (!this.question.answers.filter(e => e.is_correct).length) {
                this.alertAnswerCorrectRequired = true
                passed = false
            }

            if (this.question.answers.length > 15) {
                this.alertAnswerLimit = true
                passed = false
            }

            const payload = {
                section: 'answer',
                questionIndex: this.questionIndex,
                passed
            }

            this.$emit('validate-result', payload)

            return true
        },

        toggleFullWidthImageMobile(event) {
            this.$store.commit('toggleFullWidthImageMobile', {
                questionIndex: this.questionIndex,
                value: event.target.checked
            })
        }
    }
}
</script>

<style lang="scss">
.question-setting-section {
    .question-input {
        padding: 0.375rem 2.5rem 0.35rem 0.75rem !important;
    }

    .question-video-box {
        max-width: 640px;
        width: 100%;
        aspect-ratio: 16 / 9;
        border: 2px dashed rgb(204, 204, 204);
        border-radius: 0.25rem;

        .remove-video-icon {
            top: 0.25rem;
            right: 0.25rem;
        }

        .edit-video-icon {
            top: 2rem;
            right: 0.25rem;
        }
    }

    .pieces-select-box {
        column-gap: 0.5rem;
    }

    .track-select-box {
        border: 2px dashed rgb(204, 204, 204);
        border-radius: 0.25rem;

        .empty-track {
            color: rgb(204, 204, 204);
        }
    }

    .track-badge {
        row-gap: 0.5rem;
        border: 1px solid #ccc;
        border-radius: 0.25rem;
    }
}
</style>
